import * as React from "react";
import { Button, FormGroup, FormControl, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { History, Location } from "history";

import type { LoginUserObj } from "@/lib/models";
import * as Auth from "@/lib/auth";
import { getBackend } from "@/lib/backend/backend2";
import LoginPinModal from "./LoginPinModal";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import * as BusinessLogic from "@/lib/businessLogic";
import { FormattedMessage } from "react-intl";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";

import img_oauth_provider_azure from "@/img/oauth_provider_azure.svg";

type Props = {
    history: History;
    location: Location;
};

type State = {
    user: LoginUserObj;
    showPinModal: boolean;
    showPinButton: boolean;
    showRegisterLink: boolean;
    message: string;
    providers: string[];
    showOtp: boolean;
    showPassword: boolean;
};

const env_flag = BusinessLogic.getSysFlag("env");

class Login extends React.Component<Props, State> {
    state: State = {
        user: {
            username: "",
            password: "",
            remember: false,
            otp: "",
        },
        showRegisterLink: false,
        showPinButton: false,
        showPinModal: false,
        message: "",
        providers: [],
        showOtp: false,
        showPassword: true,
    };

    async componentDidMount() {
        // check query params if they match to unior org_code and redirect to SSO login
        if (document !== undefined && window.location !== undefined) {
            const params = new URLSearchParams(window.location.search);
            if (params !== undefined) {
                // get org_code from query params
                const org_code = params.get("org_code");
                if (org_code != undefined) {
                    // log in user with sso
                    this.loginOauth2("external_provider", org_code);
                }
            }
        }

        try {
            await BusinessLogic.loadFeatureMatrixAsync();
            const showRegisterLink = false; // getSysFlag("integration").toLowerCase().indexOf("kolektor") < 0;
            const pin = await getBackend().common.canPinLoginFromCurrentLocation({});
            this.setState({ showPinButton: pin.pin_login_allowed, showRegisterLink });
            await this.getOauthProviders();
        } catch (e) {
            console.error(e);
        }
    }

    validateForm() {
        return this.state.user.username.length > 0 && this.state.user.password.length > 0;
    }

    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const field = target.id;
        const value = target.type === "checkbox" ? target.checked : target.value;
        // update
        this.setState(prev_state => ({
            ...prev_state,
            user: {
                ...prev_state.user,
                [field]: value,
            },
        }));
    };

    redirectAfterSuccess = () => {
        const { history, location } = this.props;
        const search_params = new URLSearchParams(location.search);
        const redirect = search_params.get("redirect") || Auth.getLoginRedirectURL();
        const reload = search_params.get("reload") !== null;

        if (reload) {
            window.location.href = redirect;
        } else {
            history.push(redirect);
        }
    };

    handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            const u = this.state.user;
            const res = await Auth.loginUP(u.username, u.password.trim(), u.remember, u.otp);
            this.setState({ message: "" });
            if (res) {
                this.setState({ showOtp: true });
            } else {
                if (Auth.getLoggedinUser().must_reset) {
                    this.props.history.push("/change_password");
                } else if (Auth.getLoggedinUser().must_activate_twofa) {
                    this.props.history.push("/activate_twofa");
                } else {
                    this.redirectAfterSuccess();
                }
            }
        } catch (err) {
            console.log(err);
            this.setState({ message: (err as Error).message });
        }
    };

    renderAlert() {
        return (
            <div className="row">
                <div className="col-12">
                    <ErrorComponent msg={this.state.message} type="warning" />
                </div>
            </div>
        );
    }

    getOauthProviders = async () => {
        try {
            const providers = await Auth.oauth2Providers();
            this.setState({ providers });
        } catch (err) {
            this.setState({ message: (err as Error).message });
        }
    };

    loginOauth2 = async (provider: string, org_code?: string) => {
        try {
            await Auth.oauth2Initiate(provider, "login", this.state.user.username.trim(), org_code);
        } catch (err) {
            this.setState({ message: (err as Error).message });
        }
    };

    renderOauthProvider = (type: string, title: string, img_url?: string) => {
        if (type === "external_provider") {
            const switchingButton = (
                <Button
                    block
                    key={`button-oauth-provider-${type}`}
                    className="btn_register btn-secondary"
                    onClick={() => {
                        this.setState(prevState => ({
                            showPassword: !prevState.showPassword,
                        }));
                    }}
                >
                    {!this.state.showPassword && (
                        <span>{translate("common.sign_in_with_password", "Sign In with password")}</span>
                    )}

                    {this.state.showPassword && (
                        <span>{translate("common.sign_in_with_username_only", "Username only")}</span>
                    )}
                </Button>
            );

            return switchingButton;
        }

        if (img_url !== undefined) {
            return (
                <button type="button" className="btn btn-default">
                    {" "}
                    <img
                        src={img_url}
                        alt={title}
                        onClick={() => {
                            this.loginOauth2(type);
                        }}
                    />{" "}
                </button>
            );
        }
        return (
            <Button
                block
                className="btn_register btn-primary"
                key={type}
                onClick={() => {
                    this.loginOauth2(type);
                }}
            >
                {title}
            </Button>
        );
    };

    renderOauthProviders = () => {
        if (this.state.providers.length === 0) {
            return null;
        }
        const res: React.ReactNode[] = [];
        if (this.state.providers.indexOf("github") >= 0) {
            res.push(this.renderOauthProvider("github", "GitHub"));
        }
        if (this.state.providers.indexOf("azure-ad") >= 0) {
            res.push(
                this.renderOauthProvider("azure-ad", "Microsoft Azure Active Directory", img_oauth_provider_azure)
            );
        }
        if (this.state.providers.indexOf("adfs") >= 0) {
            res.push(this.renderOauthProvider("adfs", "Active Directory"));
        }
        if (this.state.providers.indexOf("external_provider") >= 0) {
            res.push(this.renderOauthProvider("external_provider", "Sign with SSO"));
        }
        return (
            <>
                <hr key="hr_pin" />
                <div className="text-center">{res}</div>
            </>
        );
    };

    renderPinEntry = () => {
        if (!this.state.showPinButton) {
            return null;
        }
        return (
            <>
                <hr key="hr_external_id" />
                <LoginPinModal
                    onCancel={() => {
                        this.setState({ showPinModal: false });
                    }}
                    show={this.state.showPinModal}
                    onSuccess={() => {
                        this.setState({ showPinModal: false });
                        this.redirectAfterSuccess();
                    }}
                />
                <Button
                    block
                    style={{ width: "auto" }}
                    className="btn_register"
                    key="btnPin"
                    onClick={() => {
                        this.setState({ showPinModal: true });
                    }}
                >
                    {translate("common.enter_with_pin", "Enter with PIN number")}
                </Button>
            </>
        );
    };

    renderInputFields = () => {
        if (!this.state.showOtp) {
            return (
                <>
                    {this.state.message !== "" && this.renderAlert()}
                    <FormGroup controlId="username" className="form-label-group">
                        <FormControl
                            autoFocus
                            type="text"
                            placeholder="Your email address"
                            value={this.state.user.username}
                            onChange={this.handleChange}
                        />
                        <Form.Label>{translate("common.email", "E-mail address")}</Form.Label>
                    </FormGroup>

                    {this.state.showPassword && (
                        <FormGroup controlId="password" className="form-label-group">
                            <FormControl
                                type="password"
                                placeholder="Your password"
                                value={this.state.user.password}
                                onChange={this.handleChange}
                            />
                            <Form.Label>{translate("common.password", "Password")}</Form.Label>
                        </FormGroup>
                    )}

                    <div className="row">
                        <div className="col-6">
                            <FormGroup controlId="remember" bsPrefix="custom-control custom-checkbox checkbox">
                                <FormControl
                                    type="checkbox"
                                    checked={this.state.user.remember}
                                    onChange={this.handleChange}
                                    bsPrefix="custom-control-input"
                                />
                                <label className="custom-control-label" htmlFor="remember">
                                    {translate("common.remember_me", "Remember me")}
                                </label>
                            </FormGroup>
                        </div>

                        <div className="col-6">
                            {env_flag !== "dev" && (
                                <Link to="/recover_password" className="blue_link text-right">
                                    {translate("common.forgot_password", "Forgot your password?")}
                                </Link>
                            )}
                        </div>
                    </div>

                    {this.state.showPassword && (
                        <Button
                            block
                            className="btn_register btn-primary"
                            disabled={!this.validateForm()}
                            type="submit"
                        >
                            {translate("common.sign_in", "Sign In")}
                        </Button>
                    )}

                    {!this.state.showPassword && (
                        <Button
                            block
                            className="btn_register btn-primary"
                            onClick={() => {
                                this.loginOauth2("external_provider");
                            }}
                        >
                            {translate("common.sign_in", "Sign In")}
                        </Button>
                    )}

                    {this.state.showRegisterLink && (
                        <Link to="/register" className="blue_link text-center">
                            {translate("common.create_account", "Create an account")}
                        </Link>
                    )}

                    {this.renderPinEntry()}
                    {this.renderOauthProviders()}
                </>
            );
        } else {
            return (
                <>
                    {this.state.message !== "" && this.renderAlert()}
                    <div>
                        <FormattedMessage
                            id="common.twofa_success_text"
                            defaultMessage="Type generated OTP (One Time Passowrd) into the input field."
                        />
                    </div>
                    <FormGroup controlId="otp" className="form-label-group">
                        <FormControl
                            autoFocus
                            type="text"
                            placeholder="Your otp"
                            value={this.state.user.otp}
                            onChange={this.handleChange}
                        />
                        <Form.Label>{translate("common.otp", "OTP")}</Form.Label>
                    </FormGroup>
                    <Button block className="btn_register btn-primary" disabled={!this.validateForm()} type="submit">
                        {translate("common.sign_in", "Sign In")}
                    </Button>
                </>
            );
        }
    };

    render() {
        return (
            <section id="register" key="1">
                <div className="caption_div">
                    <form className="form_box" onSubmit={this.handleSubmit}>
                        <h3>{translate("common.sign_in", "Sign In")}</h3>
                        {this.renderInputFields()}
                    </form>
                </div>
            </section>
        );
    }
}

export default Login;
